import React, { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PollSelectors, PollActions } from "@Store";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "@Store";
import { IPoll } from "@Interfaces";
import { withPublicLayout } from "@Hocs/index";
import { Grid, Typography, Divider } from "@mui/material";
import { compose } from "redux";
import { pretty, listify, FormattedText } from "./reportHelpers";
import VotesCountPieChart from "@Components/Graphs/VotesCountApex/VotesCount";
import VotesOverTimeLineChart from "@Components/Graphs/VotesOverTimeApex/VotesOverTime";
import AgeDistributionChart from "@Components/Graphs/AgeDistributionApex/AgeDistribution";
import ProfessionDistributionTreeMap from "@Components/Graphs/ProfessionDistributionApex/ProfessionDistribution";
import SectorDistributionTreeMap from "@Components/Graphs/SectorDistributionApex/SectorDistribution";
import IndustryDistributionTreeMap from "@Components/Graphs/IndustryDistributionApex/IndustryDistribution";
import RoleDistributionTreeMap from "@Components/Graphs/RoleDistributionApex/RoleDistribution";
import OrganizationTypeDistributionTreeMap from "@Components/Graphs/OrganizationTypeDistributionApex/OrganizationTypeDistribution";
import GenderVoteDistributionChart from "@Components/Graphs/GenderDistributionVotesApex/GenderDistributionVotes";
import GenderDistributionChart from "@Components/Graphs/GenderDistributionApex/GenderDistribution";
import CustomVariableCharts from "@Components/Graphs/CustomVariableApex/CustomVariableCharts";
import CountryDistributionChart from "@Components/Graphs/CountryDistributionApex/CountryDistribution";
import NationalityDistributionChart from "@Components/Graphs/NationalityDistributionApex/NationalityDistribution";

import {
    Question,
    Story,
    GraphContainer,
    SectionHeaderBox,
} from "@Styled/Pages/Report.styled";

interface CustomizedState {
    channelId: string;
    question: string;
}

const ReportPage: React.FC<any> = () => {
    const { pollId: pollIdParam } = useParams<"pollId">();
    const pollId = pollIdParam || "";
    const dispatch = useDispatch<any>();
    const { t } = useTranslation();
    const location = useLocation();
    const locationState = location.state as CustomizedState | undefined;
    const channelIdFromState = locationState?.channelId;
    const questionFromState = locationState?.question;
    const pollLookup = PollSelectors.selectPollWithChannelByPollId(pollId);
    const resolvedChannelId = channelIdFromState || pollLookup?.channelId;
    const currentPoll = useSelector(
        (state: RootStateType) => state.poll
    ) as IPoll | null;
    const questionFromSinglePoll = currentPoll?.question || "";
    const question =
        questionFromState ||
        pollLookup?.poll?.question ||
        questionFromSinglePoll ||
        "";
    const reportFromChannels = PollSelectors.selectReportByPollId(
        resolvedChannelId,
        pollId
    );
    const reportFromSinglePoll = currentPoll?.report;
    const reportData = reportFromChannels || reportFromSinglePoll;
    useEffect(() => {
        if (!reportData && pollId) {
            dispatch(PollActions.fetchPollReport(pollId));
        }
        if (!questionFromSinglePoll && pollId) {
            dispatch(PollActions.fetchSinglePoll(pollId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, pollId]);

    const distributions = reportData?.custom_variable_distributions;
    const byVote = reportData?.custom_variable_by_vote;
    const genderDist = reportData?.gender_distribution;
    const ageDist = reportData?.age_distribution;
    const professionDist = reportData?.profession_distribution;
    const sectorDist = reportData?.sector_distribution;
    const industryDist = reportData?.industry_distribution;
    const roleDist = reportData?.role_distribution;
    const organizationTypeDist = reportData?.organization_type_distribution;
    const countryDist = reportData?.country_distribution;
    const nationalityDist = reportData?.nationality_distribution;

    const variableLabels = useMemo<string[]>(
        () => (distributions ? Object.keys(distributions).map(pretty) : []),
        [distributions]
    );

    const hasCustoms = !!distributions && !!byVote && variableLabels.length > 0;

    const hasGender =
        genderDist &&
        Array.isArray(genderDist) &&
        genderDist.some(
            (row: any) =>
                row.gender?.toLowerCase() !== "unknown" && (row.count || 0) > 0
        );

    const hasAge =
        ageDist &&
        Array.isArray(ageDist) &&
        ageDist.some((row: any) => {
            const ageRangeLabel = (
                row.agerange ||
                row.date_range ||
                ""
            ).toString();
            return (
                ageRangeLabel.trim().toLowerCase() !== "unknown" &&
                (row.count || 0) > 0
            );
        });

    const hasProfession =
        professionDist &&
        Array.isArray(professionDist) &&
        professionDist.some(
            (row: any) =>
                row.profession?.toLowerCase() !== "unknown" &&
                (row.count || 0) > 0
        );

    const hasSector =
        sectorDist &&
        Array.isArray(sectorDist) &&
        sectorDist.some(
            (row: any) =>
                row.sector?.toLowerCase() !== "unknown" && (row.count || 0) > 0
        );

    const hasIndustry =
        industryDist &&
        Array.isArray(industryDist) &&
        industryDist.some(
            (row: any) =>
                row.industry?.toLowerCase() !== "unknown" &&
                (row.count || 0) > 0
        );

    const hasRole =
        roleDist &&
        Array.isArray(roleDist) &&
        roleDist.some(
            (row: any) =>
                row.role?.toLowerCase() !== "unknown" && (row.count || 0) > 0
        );

    const hasOrganizationType =
        organizationTypeDist &&
        Array.isArray(organizationTypeDist) &&
        organizationTypeDist.some(
            (row: any) =>
                row.organization_type?.toLowerCase() !== "unknown" &&
                (row.count || 0) > 0
        );

    const hasCountry =
        countryDist &&
        Array.isArray(countryDist) &&
        countryDist.some(
            (row: any) =>
                row.country?.toLowerCase() !== "unknown" && (row.count || 0) > 0
        );

    const hasNationality =
        nationalityDist &&
        Array.isArray(nationalityDist) &&
        nationalityDist.some(
            (row: any) =>
                row.nationality?.toLowerCase() !== "unknown" &&
                (row.count || 0) > 0
        );

    const hasAnyDemographics =
        hasGender ||
        hasAge ||
        hasProfession ||
        hasSector ||
        hasIndustry ||
        hasRole ||
        hasOrganizationType ||
        hasCountry ||
        hasNationality;

    const filterDistributionEntries = (
        rows: any[] | undefined,
        key: string,
        limit = 10
    ) =>
        (rows || [])
            .filter(
                (row) =>
                    row &&
                    row[key] &&
                    typeof row[key] === "string" &&
                    row[key].trim().toLowerCase() !== "unknown" &&
                    (row.count || 0) > 0
            )
            .sort((a, b) => (b.count || 0) - (a.count || 0))
            .slice(0, limit);

    const countryTopEntries = filterDistributionEntries(countryDist, "country");
    const nationalityTopEntries = filterDistributionEntries(
        nationalityDist,
        "nationality"
    );

    const subtitle = hasCustoms
        ? t("reports.customVariables.subtitle", {
              variables: listify(variableLabels),
          })
        : undefined;
    const recommendationText = reportData?.report_story?.recommendation;

    if (!reportData) return <div>{t("reports.loading")}</div>;

    return (
        <div>
            <Grid container spacing={4}>
                {/* HEADER */}
                <Grid item xs={12}>
                    <Question>{question}</Question>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        align="center"
                        gutterBottom
                    >
                        {t("reports.audience_insight")}
                    </Typography>
                    <Divider style={{ marginTop: 8 }} />
                </Grid>
                {/* INSIGHTS (top narrative paragraph only) */}
                {reportData?.report_story?.summary && (
                    <>
                        <Grid item xs={12}>
                            <SectionHeaderBox>
                                <Typography variant="h6" align="left">
                                    {t("reports.insights.heading")}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    align="left"
                                >
                                    {t("reports.insights.subtitle")}
                                </Typography>
                            </SectionHeaderBox>
                        </Grid>
                        <Grid item xs={12}>
                            <Story>
                                <FormattedText
                                    text={reportData?.report_story?.summary}
                                />
                            </Story>
                        </Grid>
                    </>
                )}

                {/* MAIN RESULT & TREND */}
                <Grid item xs={12}>
                    <SectionHeaderBox>
                        <Typography variant="h6" align="left">
                            {t("reports.overall.heading")}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            align="left"
                        >
                            {t("reports.overall.description")}
                        </Typography>
                    </SectionHeaderBox>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="subtitle1"
                                align="center"
                                style={{ fontWeight: "bold" }}
                            >
                                {t("reports.overall.distribution.title")}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                gutterBottom
                                align="center"
                            >
                                {t("reports.overall.distribution.description")}
                            </Typography>
                            <GraphContainer>
                                <VotesCountPieChart
                                    votesCount={reportData?.votes_count}
                                />
                            </GraphContainer>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="subtitle1"
                                align="center"
                                style={{ fontWeight: "bold" }}
                            >
                                {t("reports.overall.trend.title")}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                gutterBottom
                                align="center"
                            >
                                {t("reports.overall.trend.description")}
                            </Typography>
                            <GraphContainer>
                                <VotesOverTimeLineChart
                                    votesCountByTime={
                                        reportData?.votes_count_by_time
                                    }
                                />
                            </GraphContainer>
                        </Grid>
                    </Grid>
                </Grid>

                {/* CUSTOM DIMENSIONS */}
                {hasCustoms && (
                    <>
                        <Grid item xs={12}>
                            <SectionHeaderBox>
                                <Typography
                                    variant="h6"
                                    style={{ marginTop: 8 }}
                                    align="left"
                                >
                                    {t("reports.customVariables.heading")}
                                </Typography>
                                {subtitle && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        align="left"
                                    >
                                        {subtitle}
                                    </Typography>
                                )}
                            </SectionHeaderBox>
                        </Grid>
                        <Grid item xs={12}>
                            <CustomVariableCharts
                                distributions={distributions}
                                byVote={byVote}
                            />
                        </Grid>
                    </>
                )}
                {/* DEMOGRAPHICS */}
                {hasAnyDemographics && (
                    <>
                        <Grid item xs={12}>
                            <SectionHeaderBox>
                                <Typography
                                    variant="h6"
                                    style={{ marginTop: 8 }}
                                    align="left"
                                >
                                    {t("reports.demographics.title")}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    align="left"
                                >
                                    {t("reports.demographics.description")}
                                </Typography>
                            </SectionHeaderBox>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={3}>
                                {hasGender && (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                variant="subtitle1"
                                                align="center"
                                                gutterBottom
                                                style={{ fontWeight: "bold" }}
                                            >
                                                {t(
                                                    "reports.demographics.genderVote.title"
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                align="center"
                                                gutterBottom
                                            >
                                                {t(
                                                    "reports.demographics.genderVote.subtitle"
                                                )}
                                            </Typography>
                                            <GraphContainer>
                                                <GenderVoteDistributionChart
                                                    genderVoteData={
                                                        reportData?.gender_by_vote
                                                    }
                                                />
                                            </GraphContainer>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                variant="subtitle1"
                                                align="center"
                                                gutterBottom
                                                style={{ fontWeight: "bold" }}
                                            >
                                                {t(
                                                    "reports.demographics.genderBreakdown.title"
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                align="center"
                                                gutterBottom
                                            >
                                                {t(
                                                    "reports.demographics.genderBreakdown.subtitle"
                                                )}
                                            </Typography>
                                            <GraphContainer>
                                                <GenderDistributionChart
                                                    data={genderDist}
                                                />
                                            </GraphContainer>
                                        </Grid>
                                    </>
                                )}
                                {hasAge && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle1"
                                            align="center"
                                            gutterBottom
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {t(
                                                "reports.demographics.age.title"
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            align="center"
                                            gutterBottom
                                        >
                                            {t(
                                                "reports.demographics.age.subtitle"
                                            )}
                                        </Typography>
                                        <GraphContainer>
                                            <AgeDistributionChart
                                                ageDistribution={ageDist}
                                            />
                                        </GraphContainer>
                                    </Grid>
                                )}
                                {hasCountry && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle1"
                                            align="center"
                                            gutterBottom
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {t(
                                                "reports.demographics.country.title"
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            align="center"
                                            gutterBottom
                                        >
                                            {t(
                                                "reports.demographics.country.subtitle"
                                            )}
                                        </Typography>
                                        <GraphContainer>
                                            <CountryDistributionChart
                                                data={countryTopEntries}
                                            />
                                        </GraphContainer>
                                    </Grid>
                                )}
                                {hasNationality && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle1"
                                            align="center"
                                            gutterBottom
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {t(
                                                "reports.demographics.nationality.title"
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            align="center"
                                            gutterBottom
                                        >
                                            {t(
                                                "reports.demographics.nationality.subtitle"
                                            )}
                                        </Typography>
                                        <GraphContainer>
                                            <NationalityDistributionChart
                                                data={nationalityTopEntries}
                                            />
                                        </GraphContainer>
                                    </Grid>
                                )}
                                {hasSector && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle1"
                                            align="center"
                                            gutterBottom
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {t(
                                                "reports.demographics.sector.title"
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            align="center"
                                            gutterBottom
                                        >
                                            {t(
                                                "reports.demographics.sector.subtitle"
                                            )}
                                        </Typography>
                                        <GraphContainer>
                                            <SectorDistributionTreeMap
                                                sectorDistribution={sectorDist}
                                            />
                                        </GraphContainer>
                                    </Grid>
                                )}
                                {hasIndustry && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle1"
                                            align="center"
                                            gutterBottom
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {t(
                                                "reports.demographics.industry.title"
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            align="center"
                                            gutterBottom
                                        >
                                            {t(
                                                "reports.demographics.industry.subtitle"
                                            )}
                                        </Typography>
                                        <GraphContainer>
                                            <IndustryDistributionTreeMap
                                                industryDistribution={
                                                    industryDist
                                                }
                                            />
                                        </GraphContainer>
                                    </Grid>
                                )}
                                {hasOrganizationType && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle1"
                                            align="center"
                                            gutterBottom
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {t(
                                                "reports.demographics.organizationType.title"
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            align="center"
                                            gutterBottom
                                        >
                                            {t(
                                                "reports.demographics.organizationType.subtitle"
                                            )}
                                        </Typography>
                                        <GraphContainer>
                                            <OrganizationTypeDistributionTreeMap
                                                organizationTypeDistribution={
                                                    organizationTypeDist
                                                }
                                            />
                                        </GraphContainer>
                                    </Grid>
                                )}
                                {hasProfession && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle1"
                                            align="center"
                                            gutterBottom
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {t(
                                                "reports.demographics.profession.title"
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            align="center"
                                            gutterBottom
                                        >
                                            {t(
                                                "reports.demographics.profession.subtitle"
                                            )}
                                        </Typography>
                                        <GraphContainer>
                                            <ProfessionDistributionTreeMap
                                                professionDistribution={
                                                    professionDist
                                                }
                                            />
                                        </GraphContainer>
                                    </Grid>
                                )}
                                {hasRole && (
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle1"
                                            align="center"
                                            gutterBottom
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {t(
                                                "reports.demographics.role.title"
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            align="center"
                                            gutterBottom
                                        >
                                            {t(
                                                "reports.demographics.role.subtitle"
                                            )}
                                        </Typography>
                                        <GraphContainer>
                                            <RoleDistributionTreeMap
                                                roleDistribution={roleDist}
                                            />
                                        </GraphContainer>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </>
                )}

                {/* RECOMMENDATION */}
                {recommendationText && (
                    <>
                        <Grid item xs={12}>
                            <SectionHeaderBox>
                                <Typography
                                    variant="h6"
                                    style={{ marginTop: 8 }}
                                    align="left"
                                >
                                    {t("reports.recommendation.title")}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    align="left"
                                >
                                    {t("reports.recommendation.subtitle")}
                                </Typography>
                            </SectionHeaderBox>
                        </Grid>
                        <Grid item xs={12}>
                            <div style={{ textAlign: "justify" }}>
                                <FormattedText
                                    text={
                                        reportData?.report_story?.recommendation
                                    }
                                />
                            </div>
                        </Grid>
                    </>
                )}
            </Grid>
        </div>
    );
};

export default compose(withPublicLayout)(ReportPage);
